/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    onAuthStateChanged,
    signOut as firebaseSignOut
} from 'firebase/auth';
import type { User } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import { type UserProfile } from '@/types/team';
import { logger } from '@/lib/logger';
import {
    NO_PLATFORM_ADMIN,
    effectiveProfile,
    platformAdminStateFromClaims,
    type PlatformAdminState,
} from '@/lib/platformAdminState';

interface AuthContextType {
    user: User | null;
    /**
     * The profile pages scope by. While a platform admin views another org this
     * is the DERIVED read-only profile for that org (src/lib/platformAdminState.ts);
     * realProfile is always the signed-in user's own users/{uid} document.
     */
    profile: UserProfile | null;
    realProfile: UserProfile | null;
    platformAdmin: PlatformAdminState;
    loading: boolean;
    /**
     * Set when the users/{uid} snapshot FAILED (e.g. permission denied), as opposed to
     * the document simply not existing. Without this the two are indistinguishable, and
     * an infrastructure fault silently routes the user down a wrong-but-plausible path.
     */
    profileError: string | null;
    signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [profileError, setProfileError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [claims, setClaims] = useState<Record<string, unknown> | null>(null);
    // Bumped when an open view reaches its expiry, so the derived profile
    // falls back to the real one without waiting for a token refresh.
    const [clock, setClock] = useState(() => Date.now());

    useEffect(() => {
        let unsubscribeProfile: (() => void) | null = null;
        
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
          if (unsubscribeProfile) {
            unsubscribeProfile();
            unsubscribeProfile = null;
          }
          if (firebaseUser) {
            setLoading(true);
            setProfile(null);
            setProfileError(null);
            setUser(firebaseUser);
            setClaims(null);
            firebaseUser.getIdTokenResult?.()
              .then((r) => setClaims(r.claims as Record<string, unknown>))
              .catch((err) => logger.error('Error reading token claims:', err));

            unsubscribeProfile = onSnapshot(
              doc(db, 'users', firebaseUser.uid),
              (docSnap) => {
                if (docSnap.exists()) {
                  setProfile(docSnap.data() as UserProfile);
                } else {
                  setProfile(null);
                }
                setProfileError(null);
                setLoading(false);
              },
              (error) => {
                logger.error('Error fetching user profile:', error);
                // Distinguish "could not read" from "does not exist". Callers must not
                // treat a failed read as an absent profile.
                setProfileError(error instanceof Error ? error.message : String(error));
                setLoading(false);
              }
            );
          } else {
            setUser(null);
            setClaims(null);
            setProfile(null);
            setProfileError(null);
            setLoading(false);
          }
        });
      
        // Cleanup function
        return () => {
          unsubscribe();
          if (unsubscribeProfile) {
            unsubscribeProfile();
          }
        };
    }, []);

    const signOut = useCallback(async () => {
        await firebaseSignOut(auth);
    }, []);

    const platformAdmin = useMemo(
        () => (claims ? platformAdminStateFromClaims(claims, clock) : NO_PLATFORM_ADMIN),
        [claims, clock]
    );

    useEffect(() => {
        if (!platformAdmin.viewExpiresAt) return;
        const ms = Math.max(0, platformAdmin.viewExpiresAt - Date.now()) + 1000;
        const timer = setTimeout(() => setClock(Date.now()), ms);
        return () => clearTimeout(timer);
    }, [platformAdmin.viewExpiresAt]);

    const viewProfile = useMemo(() => effectiveProfile(profile, platformAdmin), [profile, platformAdmin]);

    const value = useMemo(
        () => ({
            user,
            profile: viewProfile,
            realProfile: profile,
            platformAdmin,
            loading,
            profileError,
            signOut,
        }),
        [user, viewProfile, profile, platformAdmin, loading, profileError, signOut]
    );

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

/**
 * useAuth without the provider requirement: undefined outside an
 * AuthProvider. For components that only ADD behaviour when signed in and
 * are rendered in isolation in tests (EsxDownloadModal's download stamp).
 */
export const useOptionalAuth = () => useContext(AuthContext);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
